import { SourceDataVerificationPermission } from '@/api/dtos';
import ConfigurationSummaryList from '@/components/data/configuration/summary/ConfigurationSummaryList';
import PatientDataList from '@/components/data/patient/data/PatientDataList';
import Breadcrumbs from '@/components/layouts/Breadcrumbs';
import PageTitle, { TITLE_SEPARATOR } from '@/components/layouts/PageTitle';
import TrialContext from '@/contexts/data/TrialContext';
import useConfigurationSummaries from '@/hooks/data/useConfigurationSummaries';
import usePatient from '@/hooks/data/usePatient';
import usePatientDataSummaries from '@/hooks/data/usePatientDataSummaries';
import usePermissions from '@/hooks/utility/usePermissions';
import { faBoxArchive, faClipboardListCheck, faDna, faGlobeOceania, faPlus, faUser, faUsers } from '@fortawesome/duotone-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Grid, IconButton, Paper, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { FC, useContext, useCallback, useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';


const Title = styled(Typography)(({ theme }) => ({
    display: 'flex'
}));

const PatientData: FC = () => {
    
    const { trialCode, patientStudyNumber } = useParams();

    const { data: trial } = useContext(TrialContext);
    const { data: patient, loading: patientLoading, validating: patientUpdating } = usePatient(patientStudyNumber!, { revalidateOnFocus: false });
    const { data: configurations, loading: configurationsLoading, validating: configurationsUpdating } = useConfigurationSummaries(trialCode!, { revalidateOnFocus: false });

    const loadingData = configurationsLoading || patientLoading;
    const updating = !loadingData && (configurationsUpdating || patientUpdating);

    const theme = useTheme();
    
    const { 
        data: [
            hasViewPatient
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.ViewPatient
        ], 
        loadingData,
        trial?.id,
        undefined,
        undefined,
        undefined,
        patient?.institutionId,
        patient?.id,
        undefined,
        undefined,
        undefined,
        {
            revalidateOnFocus: false
        }
    );
    
    
    const loading = loadingData || permissionsLoading || permissionsValidating;

    const breadcrumbs = useMemo(() => {
        return [
            {
                label: 'Trials',
                link: `/`,
                icon: faDna
            },
            {
                label: 'Participants',
                link: `/${trialCode}/participants`,
                icon: faUsers
            },
            {
                label: patientStudyNumber,
                link: `/${trialCode}/participants/${patientStudyNumber}`,
                icon: faUser
            },
            {
                label: 'Data',
                icon: faBoxArchive
            }
        ];
    }, [patientStudyNumber, trialCode]);
    
    if (!hasViewPatient && !permissionsLoading && !permissionsValidating) {
        return (
            <Navigate to='/error/403' />
        )
    }
    
    return (
        <>
            <PageTitle title={`Data${TITLE_SEPARATOR}${patientStudyNumber}${TITLE_SEPARATOR}${trial?.name}`} />
            <Breadcrumbs 
                breadcrumbs={breadcrumbs}
            />
            <Title 
                variant="h1" 
                gutterBottom
                color={theme.palette.secondary.main}
            >
                Participant {patientStudyNumber} &ndash; Data
            </Title>
            <Grid 
                container
                spacing={2}
            >
                <Grid 
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={8}
                    xl={9}
                >
                    <Paper>
                        <PatientDataList 
                            configurations={configurations}
                            patient={patient}
                            trial={trial} 
                            loading={loading} 
                            updating={updating}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </>
    );
}

export default PatientData;